import { Button, Checkbox, Col, Grid, Modal, Row, Space, theme, Typography, Input, message, Select, Slider, Form, Result, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlanCard } from './PlanCard/PlanCard';
import { LeftOutlined, RightOutlined, CheckCircleFilled } from '@ant-design/icons';
import { COIN_THRESHOLD } from './constants';
import { RTL_LANGUAGES } from '../../../Constants/language';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import { useGlobal } from '../../Shared/GlobalContext/GlobalContext';
import { createCheckoutSession, getProducts, validatePromoCode } from '../BillingServices';
import { resolveBackendMessage } from '../../../Utils/apiMessages';
import { PlanCardSkeleton } from './PlanCardSkeleton/PlanCardSkeleton';
import useMobileFullscreenModalBackHandler from '../../../Hooks/useMobileFullscreenModalBackHandler';

const { useBreakpoint } = Grid;
const { Text } = Typography;

// Utility component to inject global styles
const GlobalStyle = ({ isMobile }) => {
    if (!isMobile) return null;

    return (
        <style jsx global>{`
            .plans-mobile-modal .ant-modal-content {
                padding-bottom: 0 !important;
                position: fixed !important;
                width: 100vw !important;
                height: 100% !important;
                top: 0 !important;
                left: 0 !important;

            }
            .plans-mobile-modal .ant-modal-body {
                padding-bottom: 0 !important;
            }
            .plans-mobile-modal-wrap .ant-modal .ant-modal-content {
                padding-bottom: 0 !important;
            }
            .ant-modal.plans-mobile-modal {
                padding-bottom: 0 !important;
            }
            .ant-modal.plans-mobile-modal .ant-modal-content {
                padding-bottom: 0 !important;
            }
            /* Direct targeting by class */
            .ant-modal-content {
                padding-bottom: 0 !important;
            }
            /* Target by ant-design data attributes if they exist */
            div[class*='ant-modal-content'] {
                padding-bottom: 0 !important;
            }
        `}</style>
    );
};

const Plans = ({ handleClose, autoChargeSettings }) => {
    const { user } = useGlobal();
    const { t, i18n } = useTranslation();
    const screens = useBreakpoint() || {};
    const { token } = theme.useToken();
    const isMobile = screens?.xs;
    const [currentIndex, setCurrentIndex] = useState(0);
    const [quantities, setQuantities] = useState({});
    const [productData, setProductData] = useState({ data: [] });
    const { loading, setLoading } = useGlobal();
    const { handleError } = useErrorHandler();
    const [promoCode, setPromoCode] = useState('');
    const [discount, setDiscount] = useState(null);
    const [isEditingAutoCharge, setIsEditingAutoCharge] = useState(false);
    const [selectedAutoChargeProduct, setSelectedAutoChargeProduct] = useState(null);
    const [autoChargeThreshold, setAutoChargeThreshold] = useState(COIN_THRESHOLD);
    const [enableAutoCharge, setEnableAutoCharge] = useState(false);
    const [validatePromoLoading, setValidatePromoLoading] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [isOffline, setIsOffline] = useState(typeof navigator !== 'undefined' ? !navigator.onLine : false);

    const isRtl = RTL_LANGUAGES.includes(i18n.language.split('-')[0]);

    // When the plans modal is fullscreen on mobile, consume system back to close it first
    useMobileFullscreenModalBackHandler(!!isMobile, handleClose);

    const fetchProductData = async () => {
        setLoading(true);
        setFetchError(null);
        try {
            const data = await getProducts();
            setProductData(data);
        } catch (error) {
            handleError(error);
            setFetchError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProductData();
    }, []);

    useEffect(() => {
        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        }
    }, []);

    // Set default auto-charge product when product data loads
    useEffect(() => {
        if (productData.data.length > 0 && !selectedAutoChargeProduct) {
            const firstAvailableProduct = productData.data.find(p => !p.isCustom);
            if (firstAvailableProduct) {
                setSelectedAutoChargeProduct(firstAvailableProduct.id);
                // Set threshold to 25% of the default plan's coins
                setAutoChargeThreshold(Math.floor(firstAvailableProduct.coins * 0.25));
            }
        }
    }, [productData.data, selectedAutoChargeProduct]);

    const getPlansPerView = () => {
        if (screens.xxl) return 5;
        if (screens.xl) return 4;
        if (screens.lg) return 3;
        if (screens.md) return 2;
        if (screens?.sm) return 2;
        if (screens?.xs) return 1;
        return 4;
    };

    const { discountable, nonDiscountable, subtotal, total, discountAmount } = React.useMemo(() => {
        let disc = 0;
        let nonDisc = 0;
        productData.data.forEach((plan, index) => {
            const price = parseFloat(plan.price);
            const quantity = quantities[index] || 0;
            if (plan.loyalty_level > 0) {
                nonDisc += price * quantity;
            } else {
                disc += price * quantity;
            }
        });

        let finalDiscountable = disc;
        if (discount) {
            switch (discount.discount_type) {
                case "percentage":
                    finalDiscountable = (disc * (1 - (discount.value / 100)));
                    break;
                case "amount":
                    finalDiscountable = Math.max(0, disc - discount.value);
                    break;
                default:
                    break;
            }
        } else if (user?.discount_percentage) {
            finalDiscountable = (disc * (1 - parseFloat(user.discount_percentage) / 100));
        }

        const calculatedTotal = finalDiscountable + nonDisc;
        return {
            discountable: disc,
            nonDiscountable: nonDisc,
            subtotal: disc + nonDisc,
            total: calculatedTotal,
            discountAmount: (disc + nonDisc) - calculatedTotal
        };
    }, [productData.data, quantities, discount, user?.discount_percentage]);
    const hasNonDiscountable = nonDiscountable > 0;
    const hasPotentialDiscount = !!promoCode || !!user?.discount_percentage;

    const handleCheckout = async () => {
        const lineItems = [];
        productData.data.forEach((plan, index) => {
            if (quantities[index] > 0) {
                lineItems.push({
                    product_id: plan.id,
                    quantity: quantities[index],
                });
            }
        });

        try {
            setCheckoutLoading(true);
            const { data } = await createCheckoutSession({
                products: lineItems,
                promotion_code: promoCode || undefined,
                auto_reload_product_id: enableAutoCharge ? selectedAutoChargeProduct : undefined,
                auto_reload_threshold: enableAutoCharge ? autoChargeThreshold : undefined
            });
            window.location.href = data.checkout_url;
        } catch (error) {
            handleError(error);
        }
        finally {
            setCheckoutLoading(false);
        }
    };

    const handleApplyPromo = async () => {
        try {
            setValidatePromoLoading(true);
            const { data, ...payload } = await validatePromoCode({ promotion_code: promoCode })
            setDiscount(data);
            {
                const text = resolveBackendMessage(t, payload);
                if (text) message.success(text);
            }
        } catch (error) {
            handleError(error);
        } finally {
            setValidatePromoLoading(false);
        }
    };

    const handleRemovePromo = () => {
        setPromoCode('');
        setDiscount(null);
        message.success(t('promo_removed'));
    };

    const plansPerView = getPlansPerView();
    const maxIndex = productData.data.length - plansPerView;
    const canGoLeft = currentIndex > 0;
    const canGoRight = currentIndex < maxIndex;

    const handlePrev = () => canGoLeft && setCurrentIndex(prev => prev - 1);
    const handleNext = () => canGoRight && setCurrentIndex(prev => prev + 1);

    const visiblePlans = productData.data.slice(currentIndex, currentIndex + plansPerView);

    const arrowStyle = {
        position: 'absolute',
        top: '50%',
        transform: isRtl ? 'rotate(180deg) translate(50%)' : 'translateY(-50%)',
        fontSize: 24,
        zIndex: 1000,
        backgroundColor: token.colorBgElevated,
        color: token.colorText,
        borderRadius: '50%',
        boxShadow: token.boxShadow,
        padding: 8,
        cursor: 'pointer',
        transition: 'all 0.3s',
        '&:hover': {
            backgroundColor: token.colorBgTextHover,
            boxShadow: token.boxShadowSecondary
        }
    };

    const autoChargeContent = (
        <Col xs={24} md={12}>
            <div style={{
                padding: 16,
                borderRadius: 8,
                background: token.colorBgContainer,
                border: `1px solid ${token.colorBorderSecondary}`,
                marginTop: isMobile ? 16 : 0,
                boxSizing: 'border-box',
                height: isMobile ? 'unset' : '100%',
            }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                    {autoChargeSettings?.enabled && !isEditingAutoCharge ? (
                        <>
                            <Row justify="space-between" align="middle">
                                <Text strong style={{ fontSize: 16, color: token.colorTextHeading }}>
                                    {t('auto_reload_active')}
                                </Text>
                                <Button
                                    type="link"
                                    onClick={() => {
                                        setIsEditingAutoCharge(true);
                                        setEnableAutoCharge(true);
                                        setSelectedAutoChargeProduct(autoChargeSettings.product_id);
                                        setAutoChargeThreshold(autoChargeSettings.threshold); // Ensure threshold is set
                                    }}
                                    style={{ color: token.colorPrimary, fontWeight: 500 }}
                                >
                                    {t('edit')}
                                </Button>
                            </Row>
                            <div>
                                <Text style={{ color: token.colorTextSecondary, fontSize: 14 }}>
                                    {t('current_product')}: <span style={{ color: token.colorText, fontWeight: 500 }}>
                                        {productData.data.find(p => p.id === autoChargeSettings.product_id)?.title}
                                    </span>
                                </Text>
                            </div>
                            <div>
                                <Text style={{ color: token.colorTextSecondary, fontSize: 14 }}>
                                    {t('current_threshold')}: <span style={{ color: token.colorText, fontWeight: 500 }}>
                                        {autoChargeSettings.threshold} {t('coins')}
                                    </span>
                                </Text>
                            </div>
                        </>
                    ) : (
                        <>
                            <Checkbox
                                checked={enableAutoCharge}
                                onChange={(e) => {
                                    setEnableAutoCharge(e.target.checked);
                                }}
                                style={{ fontSize: 14 }}
                            >
                                <Text style={{ color: token.colorText, fontSize: 14 }}>
                                    {t('enable_auto_reload')}
                                </Text>
                            </Checkbox>
                            {enableAutoCharge && (
                                <>
                                    <Select
                                        placeholder={t('select_product')}
                                        style={{ width: '100%' }}
                                        onChange={(value) => {
                                            setSelectedAutoChargeProduct(value);
                                            const selectedPlan = productData.data.find(p => p.id === value);
                                            if (selectedPlan) {
                                                // Set threshold to 25% of the plan's coins
                                                setAutoChargeThreshold(Math.floor(selectedPlan.coins * 0.25));
                                            } else {
                                                setAutoChargeThreshold(0);
                                            }
                                        }}
                                        value={selectedAutoChargeProduct}
                                        options={productData.data.filter(p => !p.isCustom).map(p => ({
                                            value: p.id,
                                            label: `${p.title} (${p.coins} ${t('coins')} - $${p.price})`
                                        }))}
                                    />
                                    <Row align="middle" gutter={16}>
                                        <Col flex="auto">
                                            <Slider
                                                min={1}
                                                max={selectedAutoChargeProduct ? Math.floor(productData.data.find(p => p.id === selectedAutoChargeProduct)?.coins * 0.5) : 0}
                                                value={autoChargeThreshold}
                                                onChange={(value) => setAutoChargeThreshold(value)}
                                                disabled={!selectedAutoChargeProduct}
                                                tooltip={{ formatter: (value) => `${value} ${t('coins')}` }}
                                            />
                                        </Col>
                                        <Col>
                                            <Text style={{ color: token.colorText, fontWeight: 500 }}>
                                                {autoChargeThreshold} {t('coins')}
                                            </Text>
                                        </Col>
                                    </Row>
                                </>
                            )}
                            <Text style={{ color: token.colorTextSecondary, fontSize: 14, marginTop: 16 }}>
                                * {t('auto_reload_explanation')}
                            </Text>
                        </>
                    )}
                </Space>
            </div>
        </Col>
    );

    const summaryContent = (
        <Col xs={24} md={12}>
            <div style={{
                padding: token.paddingLG,
                borderRadius: token.borderRadiusLG,
                background: token.colorBgContainer,
                border: `1px solid ${token.colorBorderSecondary}`,
                height: isMobile ? 'unset' : '100%',
                boxSizing: 'border-box',
            }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                    {/* Subtotal Row */}
                    <Row justify="space-between" align="middle">
                        <Text>{t('subtotal')}:</Text>
                        <Text>${subtotal.toFixed(2)}</Text>
                    </Row>

                    {/* Discount Row */}
                    <Row justify="space-between" align="middle">
                        <Text>{t('discount')}:</Text>
                        {discountAmount > 0 ? (
                            discount === null ? (
                                <Text style={{ color: token.colorSuccess }}>
                                    {`${user?.discount_percentage}%`}
                                </Text>
                            ) : (
                                <Text style={{ color: token.colorSuccess }}>
                                    {discount.discount_type === "percentage"
                                        ? `${discount.value}%`
                                        : `$${discount.value}`
                                    }
                                </Text>
                            )
                        ) : (
                            <Text>-</Text>
                        )}
                    </Row>

                    {/* Notice message for Special Packs */}
                    {hasNonDiscountable && hasPotentialDiscount && (
                        <div style={{ marginTop: -4, marginBottom: 8 }}>
                            <Text type="secondary" style={{ fontSize: '0.75rem', fontStyle: 'italic', display: 'block', textAlign: 'right' }}>
                                * {t('discount_applies_to_regular_packs_only')}
                            </Text>
                        </div>
                    )}

                    {/* Total Row */}
                    <Row justify="space-between" align="middle">
                        <Text strong>{t('total')}:</Text>
                        <Text strong style={{ color: token.colorPrimary }}>
                            ${total.toFixed(2)}
                        </Text>
                    </Row>

                    {/* Promotion Code Section */}
                    {discount === null ? (
                        <Input
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value)}
                            placeholder={t('enter_promo_code')}
                            onPressEnter={handleApplyPromo}
                            suffix={
                                <Button
                                    size="small"
                                    type="primary"
                                    loading={validatePromoLoading}
                                    onClick={handleApplyPromo}
                                    style={{ marginRight: -token.marginXXS }}
                                >
                                    {t('apply')}
                                </Button>
                            }
                            style={{
                                transition: 'all 0.3s',
                                borderColor: promoCode ? token.colorPrimaryHover : undefined,
                            }}
                        />
                    ) : (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: token.paddingXS,
                                borderRadius: token.borderRadiusSM,
                                background: token.geekblue1,
                                border: `1px solid ${token.colorSuccessBorder}`,
                            }}
                        >
                            <Space>
                                <CheckCircleFilled style={{ color: token.colorSuccess }} />
                                <Text type="success" style={{ color: token.colorSuccessText }}>
                                    {t('applied_promo')}: {promoCode}
                                </Text>
                            </Space>
                            <Button
                                onClick={handleRemovePromo}
                                type="link"
                                danger
                                style={{
                                    padding: 0,
                                    height: 'auto',
                                    color: token.colorErrorText,
                                    textDecoration: 'underline',
                                }}
                            >
                                {t('remove')}
                            </Button>
                        </div>
                    )}
                </Space>
            </div>
        </Col>
    )
    const footerContent = (
        <Row gutter={[24, 16]} style={{ marginTop: isMobile ? 16 : 24, marginBottom: isMobile ? 16 : 0 }}>
            {summaryContent}
            {autoChargeContent}
        </Row>
    );

    return (
        <>
            <GlobalStyle isMobile={isMobile} />
            <Modal
                title={t('pay_as_you_go_coin_plans')}
                open
                onOk={handleClose}
                onCancel={handleClose}
                width={isMobile ? "100%" : "80%"}
                className={isMobile ? "plans-mobile-modal" : ""}
                wrapClassName={isMobile ? "plans-mobile-modal-wrap" : ""}
                styles={{
                    header: {
                        paddingBottom: 16,
                        position: isMobile ? 'sticky' : 'static',
                        top: 0,
                        zIndex: 1001,
                        background: token.colorBgElevated
                    },
                    body: {
                        height: isMobile ? 'calc(100vh - 110px)' : 'auto',
                        overflowY: isMobile ? 'auto' : 'visible',
                        padding: isMobile ? '0 16px' : 24,
                        WebkitOverflowScrolling: 'touch'
                    },
                    footer: {
                        position: isMobile ? 'sticky' : 'static',
                        bottom: 0,
                        zIndex: 1001,
                        background: token.colorBgElevated,
                        borderTop: isMobile ? `1px solid ${token.colorBorderSecondary}` : 'none',
                        padding: isMobile ? '16px 16px' : '16px 24px',
                        marginTop: isMobile ? 8 : 0
                    },
                    mask: {
                        backgroundColor: isMobile ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 0.45)'
                    },
                    content: {
                        borderRadius: isMobile ? 0 : token.borderRadiusLG,
                        height: isMobile ? '100vh' : 'auto',
                        maxHeight: isMobile ? '100vh' : 'auto',
                        display: isMobile ? 'flex' : 'block',
                        flexDirection: 'column',
                        paddingBottom: isMobile ? 0 : undefined
                    },
                    wrapper: {
                        maxHeight: '100vh'
                    }
                }}
                style={isMobile ? {
                    top: 0,
                    maxWidth: '100%',
                    margin: 0,
                    height: '100vh',
                    overflow: 'hidden',
                    padding: 0,
                    maxHeight: '100vh',
                    position: 'fixed',
                    inset: 0
                } : {
                    maxWidth: '80%',
                }}
                centered={!isMobile}
                modalRender={isMobile ? (modal) => (
                    <div style={{ height: '100vh', width: '100vw', overflow: 'hidden' }}>
                        {modal}
                    </div>
                ) : undefined}
                footer={(fetchError || isOffline) ? null : [
                    <Button
                        key="proceed_to_checkout"
                        type="primary"
                        onClick={handleCheckout}
                        loading={checkoutLoading}
                        disabled={Object.values(quantities).every(qty => !qty || qty === 0)}
                        style={{
                            width: isMobile ? '100%' : 'auto',
                            marginTop: isMobile ? 8 : 0
                        }}
                    >
                        {t('proceed_to_checkout')}
                    </Button>
                ]}
            >

                {(isOffline || fetchError) ? (
                    <Result
                        status={isOffline ? "warning" : "error"}
                        title={isOffline ? t('no_internet_connection') : t('failed_to_load_plans')}
                        subTitle={isOffline ? t('please_check_connection_and_retry') : t('error_loading_plans', { status: fetchError?.response?.status || 'N/A' })}
                        extra={[
                            <Button type="primary" key="retry" onClick={fetchProductData} disabled={isOffline}>
                                {t('retry')}
                            </Button>
                        ]}
                    />
                ) : (
                    <>
                        <Row
                            gutter={[32, 32]}
                            justify={isMobile ? 'center' : 'start'}
                            style={{
                                marginBottom: isMobile ? 8 : 15,
                                position: 'relative',
                                marginTop: isMobile ? 8 : 0,
                                transition: 'all 0.3s ease-in-out'
                            }}
                            className="plans-row"
                        >
                            {!loading && !fetchError && (
                                <>
                                    <LeftOutlined
                                        onClick={handlePrev}
                                        style={{
                                            ...arrowStyle,
                                            insetInlineStart: isMobile ? 5 : -2,
                                            opacity: canGoLeft ? 1 : 0.25,
                                            cursor: canGoLeft ? 'pointer' : 'not-allowed',
                                            top: isMobile ? 'calc(50% - 40px)' : '50%'
                                        }}
                                    />
                                    <RightOutlined
                                        onClick={handleNext}
                                        style={{
                                            ...arrowStyle,
                                            insetInlineEnd: isMobile ? 5 : -2,
                                            opacity: canGoRight ? 1 : 0.25,
                                            cursor: canGoRight ? 'pointer' : 'not-allowed',
                                            top: isMobile ? 'calc(50% - 40px)' : '50%'
                                        }}
                                    />
                                </>
                            )}
                            {loading ? (
                                Array.from({ length: isMobile ? 1 : plansPerView }).map((_, index) => (
                                    <Col
                                        key={`skeleton-${index}`}
                                        xs={24} sm={12} md={12} lg={8} xl={6}
                                        flex={screens.xxl ? '0 0 20%' : undefined}
                                        style={{ marginBottom: 0, ...(screens.xxl ? { maxWidth: '20%' } : {}) }}
                                    >
                                        <PlanCardSkeleton />
                                    </Col>
                                ))
                            ) :
                                visiblePlans.map((plan, i) => {
                                    const originalIndex = currentIndex + i;
                                    return (
                                        <Col key={originalIndex} xs={24} sm={12} md={12} lg={8} xl={6} flex={screens.xxl ? '0 0 20%' : undefined} style={{ marginBottom: 0, ...(screens.xxl ? { maxWidth: '20%' } : {}) }}>
                                            <PlanCard
                                                {...plan}
                                                index={originalIndex}
                                                quantity={quantities[originalIndex] || 0}
                                                onQuantityChange={(newQty) => setQuantities(prev => ({ ...prev, [originalIndex]: newQty }))}
                                            />
                                        </Col>
                                    );
                                })}
                        </Row>
                        <div style={{
                            paddingBottom: 0,
                            marginBottom: 0
                        }}>
                            {footerContent}
                        </div>
                    </>
                )}
        </Modal >
    </>
);
};

export default Plans;
